import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSSEConnected } from '../hooks/useSSE'

/**
 * ChatterPlayer — Browser-based chatter playback for multi-user conversations.
 *
 * Subscribes to SSE `voice_ready` events for instant playback when a persona
 * chatters. Falls back to polling every 5s for any messages that slip through.
 * Requires a user interaction (click/keypress) before autoplay works (browser policy).
 */

interface Message {
  id: number
  voice_url?: string | null
  [key: string]: unknown
}

interface Props {
  conversationId: string
  chatterEnabled: boolean
}

// Audio context unlock — browsers block autoplay until user gesture
let audioUnlocked = false
function unlockAudio() {
  if (audioUnlocked) return
  try {
    const ctx = new AudioContext()
    ctx.resume().then(() => { audioUnlocked = true; ctx.close() })
  } catch { /* older browsers */ }
}

if (typeof document !== 'undefined') {
  document.addEventListener('click', unlockAudio, { once: false })
  document.addEventListener('keydown', unlockAudio, { once: false })
}

export default function ChatterPlayer({ conversationId, chatterEnabled }: Props) {
  const sseOk = useSSEConnected()
  const playedIds = useRef(new Set<number>())
  const baselineId = useRef<number>(0)
  const baselineSet = useRef(false)
  const queue = useRef<string[]>([])
  const playing = useRef(false)
  const currentAudio = useRef<HTMLAudioElement | null>(null)
  const speed = parseFloat(localStorage.getItem('voice-speed') || '1.3')

  function playNext() {
    if (queue.current.length === 0) {
      playing.current = false
      currentAudio.current = null
      return
    }
    playing.current = true
    const url = queue.current.shift()!
    const audio = new Audio(url)
    currentAudio.current = audio
    audio.playbackRate = speed
    audio.addEventListener('canplaythrough', () => {
      audio.play().catch(() => {
        console.warn('[chatter] Autoplay blocked — click anywhere to enable audio')
        playing.current = false
      })
    }, { once: true })
    audio.onended = () => playNext()
    audio.onerror = () => playNext()
    setTimeout(() => {
      if (audio.paused && playing.current && currentAudio.current === audio) {
        audio.play().catch(() => { playing.current = false })
      }
    }, 2000)
  }

  function enqueue(url: string) {
    queue.current.push(url)
    if (!playing.current) playNext()
  }

  // ─── SSE: instant voice_ready events ────────────────────────────
  useEffect(() => {
    if (!conversationId || !chatterEnabled) return

    const es = new EventSource('/api/events')

    es.addEventListener('voice_ready', (e) => {
      try {
        const data = JSON.parse(e.data)
        // Only play if this event is for our conversation
        if (data.conversation_id !== conversationId) return
        if (!data.voice_url) return

        const msgId = data.message_id ? Number(data.message_id) : null
        // Dedupe — don't replay if we already got this via polling
        if (msgId && playedIds.current.has(msgId)) return
        if (msgId) playedIds.current.add(msgId)

        enqueue(data.voice_url)
      } catch { /* malformed event */ }
    })

    es.onerror = () => {
      // SSE will auto-reconnect — polling is our fallback
    }

    return () => es.close()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, chatterEnabled])

  // ─── Polling fallback: catch anything SSE missed ────────────────
  const { data: messages } = useQuery({
    queryKey: ['chatter-poll', conversationId],
    queryFn: async () => {
      const res = await fetch(`/api/conversations/${conversationId}/messages?limit=20`)
      if (!res.ok) return []
      const data = await res.json()
      return (Array.isArray(data) ? data : data.messages || []) as Message[]
    },
    enabled: !!conversationId && chatterEnabled,
    refetchInterval: sseOk ? false : 15_000, // SSE-conditional polling fallback
  })

  useEffect(() => {
    if (!messages || !chatterEnabled) return

    if (!baselineSet.current && messages.length > 0) {
      baselineId.current = Math.max(...messages.map(m => m.id))
      baselineSet.current = true
      return
    }

    for (const msg of messages) {
      if (
        msg.voice_url &&
        msg.id > baselineId.current &&
        !playedIds.current.has(msg.id)
      ) {
        playedIds.current.add(msg.id)
        enqueue(msg.voice_url)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, chatterEnabled])

  // Reset on conversation change
  useEffect(() => {
    baselineSet.current = false
    baselineId.current = 0
    playedIds.current.clear()
    queue.current = []
    playing.current = false
    if (currentAudio.current) {
      currentAudio.current.pause()
      currentAudio.current = null
    }
  }, [conversationId])

  return null
}
